import { useCallback, useEffect, useState } from "react";
import { api, apiErrorMessage } from "@/lib/api";
import { PageHeader } from "@/components/PageHelpers";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { Library, Save, RotateCcw, ChevronDown, ChevronUp, CheckCircle2, ThumbsUp, ThumbsDown } from "lucide-react";

/**
 * Prompt Library — one page to inspect / override / revert every Claude
 * system prompt across the platform. Changes take effect on the next
 * request (via services_prompts.get_prompt).
 */
export default function PromptLibraryPage() {
  const [items, setItems] = useState([]);
  const [expanded, setExpanded] = useState({});
  const [drafts, setDrafts] = useState({});
  const [saving, setSaving] = useState({});
  const [ratings, setRatings] = useState(null);

  const load = useCallback(async () => {
    try {
      const [{ data }, ratRes] = await Promise.all([
        api.get("/admin/ai/prompts"),
        api.get("/admin/ai/prompts/agenda-ratings-summary").catch(() => ({ data: null })),
      ]);
      setItems(data.items || []);
      const d = {};
      (data.items || []).forEach((p) => { d[p.key] = p.value; });
      setDrafts(d);
      setRatings(ratRes.data);
    } catch (e) { toast.error(apiErrorMessage(e)); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const toggle = (key) => setExpanded((prev) => ({ ...prev, [key]: !prev[key] }));

  const save = async (key) => {
    if (!drafts[key] || drafts[key].length < 20) return toast.error("Prompt must be at least 20 characters");
    setSaving((s) => ({ ...s, [key]: true }));
    try {
      await api.put(`/admin/ai/prompts/${key}`, { value: drafts[key] });
      toast.success(`Saved ${key} · effective on next call`);
      await load();
    } catch (e) { toast.error(apiErrorMessage(e)); }
    setSaving((s) => ({ ...s, [key]: false }));
  };

  const revert = async (key) => {
    if (!window.confirm(`Revert ${key} to the code default? Any custom overrides will be lost.`)) return;
    setSaving((s) => ({ ...s, [key]: true }));
    try {
      await api.delete(`/admin/ai/prompts/${key}`);
      toast.success(`Reverted ${key} to default`);
      await load();
    } catch (e) { toast.error(apiErrorMessage(e)); }
    setSaving((s) => ({ ...s, [key]: false }));
  };

  return (
    <div>
      <PageHeader eyebrow="AI Features" title="Prompt Library" subtitle="One place to inspect and tweak the tone, guardrails, and JSON schemas of every Claude call. Changes take effect on the next request — no redeploy." />

      <Card className="border-white/10 bg-card p-4 mb-4" data-testid="prompts-summary">
        <div className="flex items-center gap-3">
          <div className="h-9 w-9 rounded-md bg-indigo-500/10 border border-indigo-500/30 flex items-center justify-center">
            <Library className="h-4 w-4 text-indigo-300" />
          </div>
          <div className="text-sm text-zinc-400">
            <b className="text-zinc-200">{items.length}</b> prompts registered · <b className="text-emerald-300">{items.filter((p) => p.is_override).length}</b> currently overridden
          </div>
        </div>
      </Card>

      {ratings && (ratings.totals?.rated_items || 0) > 0 && (
        <Card className="border-white/10 bg-card p-4 mb-4" data-testid="ratings-summary">
          <div className="flex items-center gap-2 mb-3">
            <div className="text-xs font-semibold uppercase tracking-widest text-zinc-500">Board Agenda — Admin feedback</div>
            <Badge variant="outline" className="border-white/10 text-[10px]">{ratings.totals.rated_items} rated items</Badge>
            <Badge variant="outline" className="border-emerald-500/40 text-emerald-300 bg-emerald-500/10 text-[10px]"><ThumbsUp className="h-3 w-3 mr-1" /> {ratings.totals.total_ups}</Badge>
            <Badge variant="outline" className="border-rose-500/40 text-rose-300 bg-rose-500/10 text-[10px]"><ThumbsDown className="h-3 w-3 mr-1" /> {ratings.totals.total_downs}</Badge>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div>
              <div className="text-[10px] uppercase tracking-widest text-rose-300/70 mb-1 flex items-center gap-1"><ThumbsDown className="h-3 w-3" /> Trending down — worth tuning out</div>
              {(ratings.top_avoid || []).length === 0
                ? <div className="text-[11px] text-zinc-500 italic">Nothing rated down yet.</div>
                : (ratings.top_avoid || []).map((r) => (
                  <div key={r.item_title} className="text-[11px] py-1 flex items-center justify-between gap-2" data-testid={`avoid-${r.item_title.slice(0, 12)}`}>
                    <span className="truncate text-zinc-300">{r.item_title}</span>
                    <span className="font-mono text-rose-300 flex-shrink-0">👎 {r.downs}</span>
                  </div>
                ))}
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-widest text-emerald-300/70 mb-1 flex items-center gap-1"><ThumbsUp className="h-3 w-3" /> Keepers — the AI got these right</div>
              {(ratings.top_prefer || []).length === 0
                ? <div className="text-[11px] text-zinc-500 italic">Nothing rated up yet.</div>
                : (ratings.top_prefer || []).map((r) => (
                  <div key={r.item_title} className="text-[11px] py-1 flex items-center justify-between gap-2" data-testid={`prefer-${r.item_title.slice(0, 12)}`}>
                    <span className="truncate text-zinc-300">{r.item_title}</span>
                    <span className="font-mono text-emerald-300 flex-shrink-0">👍 {r.ups}</span>
                  </div>
                ))}
            </div>
          </div>
        </Card>
      )}

      <div className="space-y-2" data-testid="prompts-list">
        {items.map((p) => {
          const isOpen = !!expanded[p.key];
          const draft = drafts[p.key] ?? p.value;
          const hasChanges = draft !== p.value;
          return (
            <Card key={p.key} className="border-white/10 bg-card" data-testid={`prompt-${p.key}`}>
              <button type="button" onClick={() => toggle(p.key)} className="w-full flex items-center justify-between px-4 py-3 text-left" data-testid={`prompt-toggle-${p.key}`}>
                <div className="flex items-center gap-3 min-w-0">
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 flex-wrap">
                      <div className="text-sm font-semibold">{p.label}</div>
                      {p.is_override && (
                        <Badge variant="outline" className="border-emerald-500/40 text-emerald-300 bg-emerald-500/10 text-[10px] uppercase tracking-widest gap-1">
                          <CheckCircle2 className="h-3 w-3" /> Custom
                        </Badge>
                      )}
                      {!p.live && (
                        <Badge variant="outline" className="border-amber-500/30 text-amber-300 text-[10px] uppercase tracking-widest">
                          not yet wired
                        </Badge>
                      )}
                    </div>
                    <div className="text-xs text-zinc-500 mt-0.5 truncate">{p.description || <span className="italic">no description</span>} · {p.value.length.toLocaleString()} chars</div>
                  </div>
                </div>
                {isOpen ? <ChevronUp className="h-3 w-3 text-zinc-500 shrink-0" /> : <ChevronDown className="h-3 w-3 text-zinc-500 shrink-0" />}
              </button>

              {isOpen && (
                <div className="px-4 pb-4 space-y-3 border-t border-white/5">
                  <div className="pt-3">
                    <div className="text-[10px] uppercase tracking-widest text-zinc-500 mb-1">Prompt body</div>
                    <Textarea
                      rows={16}
                      value={draft}
                      onChange={(e) => setDrafts((d) => ({ ...d, [p.key]: e.target.value }))}
                      className="bg-white/[0.02] border-white/10 font-mono text-xs leading-5"
                      data-testid={`prompt-body-${p.key}`}
                    />
                    <div className="text-[10px] text-zinc-500 mt-1">
                      {draft.length.toLocaleString()} chars · <span className={hasChanges ? "text-amber-300" : ""}>{hasChanges ? "unsaved changes" : "saved"}</span>
                    </div>
                  </div>

                  <div className="flex justify-between items-center">
                    <div className="flex items-center gap-2">
                      {p.updated_at && (
                        <span className="text-[10px] text-zinc-500">Last edited {new Date(p.updated_at).toLocaleString()}</span>
                      )}
                    </div>
                    <div className="flex items-center gap-2">
                      {p.is_override && (
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => revert(p.key)}
                          disabled={saving[p.key]}
                          className="border-white/10"
                          data-testid={`prompt-revert-${p.key}`}
                        >
                          <RotateCcw className="h-3 w-3 mr-1" /> Revert to default
                        </Button>
                      )}
                      <Button
                        size="sm"
                        onClick={() => save(p.key)}
                        disabled={saving[p.key] || !hasChanges}
                        className="bg-orange-500 hover:bg-orange-600"
                        data-testid={`prompt-save-${p.key}`}
                      >
                        <Save className="h-3 w-3 mr-1" /> {saving[p.key] ? "Saving…" : "Save override"}
                      </Button>
                    </div>
                  </div>
                </div>
              )}
            </Card>
          );
        })}
      </div>
    </div>
  );
}
